import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import ActionButton from '../components/ActionButton';

export default function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [hakedisMap, setHakedisMap] = useState({});
  const [emailQuote, setEmailQuote] = useState(null);
  const [emailForm, setEmailForm] = useState({ to: '', message: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const navigate = useNavigate();

  const fetchData = async () => {
    const [q, c] = await Promise.all([
      api.get('/api/quotes/'),
      api.get('/api/customers/')
    ]);
    setQuotes(q.data);
    setCustomers(c.data);

    const map = {};
    await Promise.all(q.data.map(async (quote) => {
      try {
        const res = await api.get(`/api/hakedis/quote/${quote.id}`);
        map[quote.id] = res.data;
      } catch {
        map[quote.id] = null;
      }
    }));
    setHakedisMap(map);
  };

  useEffect(() => { fetchData(); }, []);

  const handleRevise = async (id) => {
    if (!window.confirm('Bu teklifin yeni bir revizyonu oluşturulacak. Devam?')) return;
    const res = await api.post(`/api/quotes/${id}/revise`);
    navigate(`/quotes/${res.data.id}`);
  };

  const handleCopy = async (id) => {
    if (!window.confirm('Bu teklif kopyalanacak. Devam?')) return;
    const res = await api.post(`/api/quotes/${id}/copy`);
    navigate(`/quotes/${res.data.id}`);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Teklifi silmek istediğinize emin misiniz?')) return;
    try {
      await api.delete(`/api/quotes/${id}`);
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || 'Teklif silinemedi'));
    }
    fetchData();
  };

  const handlePdf = async (id, quoteNumber) => {
    try {
      const res = await api.get(`/api/quotes/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${quoteNumber}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('PDF oluşturulamadı');
    }
  };

  const handleExcel = async (id, quoteNumber) => {
    try {
      const res = await api.get(`/api/quotes/${id}/excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${quoteNumber}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Excel oluşturulamadı');
    }
  };

  const getCustomerName = (id) => {
    const c = customers.find(c => c.id === id);
    return c ? c.company_name : '-';
  };

  const openEmailModal = (q) => {
    const customer = customers.find(c => c.id === q.customer_id);
    setEmailQuote(q);
    setEmailForm({ to: customer?.email || '', message: '' });
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setSendingEmail(true);
    try {
      const payload = {};
      if (emailForm.to) payload.to = emailForm.to;
      if (emailForm.message) payload.message = emailForm.message;
      const res = await api.post(`/api/quotes/${emailQuote.id}/send-email`, payload);
      alert(res.data.message);
      setEmailQuote(null);
      fetchData();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || 'E-posta gönderilemedi'));
    } finally {
      setSendingEmail(false);
    }
  };

  const statusLabel = (s) => {
    const map = { draft: 'Taslak', sent: 'Gönderildi', approved: 'Onaylandı', rejected: 'Reddedildi' };
    const colors = { draft: 'bg-gray-100 text-gray-600', sent: 'bg-blue-100 text-blue-600', approved: 'bg-green-100 text-green-600', rejected: 'bg-red-100 text-red-600' };
    return <span className={`text-xs px-2 py-1 rounded font-medium ${colors[s] || 'bg-gray-100 text-gray-600'}`}>{map[s] || s}</span>;
  };

  const grouped = [];
  const mainQuotes = quotes.filter(q => !q.parent_quote_id);
  for (const main of mainQuotes) {
    grouped.push({ ...main, isMain: true });
    const revisions = quotes
      .filter(q => q.parent_quote_id === main.id)
      .sort((a, b) => a.revision - b.revision);
    for (const rev of revisions) {
      grouped.push({ ...rev, isMain: false });
    }
  }

  const ActionButtons = ({ q }) => (
    <div className="flex flex-col gap-1.5">
      <div className="flex gap-1.5 justify-end">
        <ActionButton icon="edit" iconClass="text-blue-600" label="Düzenle" onClick={() => navigate(`/quotes/${q.id}`)} />
        <ActionButton icon="revise" iconClass="text-amber-600" label="Revizyon" onClick={() => handleRevise(q.id)} />
        <ActionButton icon="copy" iconClass="text-emerald-600" label="Kopyala" onClick={() => handleCopy(q.id)} />
      </div>
      <div className="flex gap-1.5 justify-end">
        <ActionButton icon="pdf" iconClass="text-rose-600" label="PDF" onClick={() => handlePdf(q.id, q.quote_number)} />
        <ActionButton icon="excel" iconClass="text-green-700" label="Excel" onClick={() => handleExcel(q.id, q.quote_number)} />
        <ActionButton icon="send" iconClass="text-sky-600" label="Gönder" onClick={() => openEmailModal(q)} />
        <ActionButton icon="trash" label="Sil" danger onClick={() => handleDelete(q.id)} />
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Teklifler</h2>
        <button onClick={() => navigate('/quotes/new')} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium">
          + Yeni Teklif
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Teklif No</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Müşteri</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Proje</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Tarih</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Durum</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">USD</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">EUR</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">TRY</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Hakediş</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">İşlemler</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {grouped.map(q => (
              <tr key={q.id} className={`hover:bg-gray-50 ${!q.isMain ? 'bg-amber-50' : ''}`}>
                <td className="px-4 py-3 font-mono text-sm font-medium">
                  {!q.isMain && <span className="text-amber-400 mr-1">↳</span>}
                  <span className={q.isMain ? 'text-blue-600' : 'text-amber-600'}>{q.quote_number}</span>
                </td>
                <td className="px-4 py-3 text-sm">
                  <button onClick={() => navigate(`/customers/${q.customer_id}`)} className="text-gray-800 hover:text-blue-600 hover:underline text-left">{getCustomerName(q.customer_id)}</button>
                  {q.opportunity_id && <span className="ml-1 text-xs" title="CRM fırsatına bağlı">💼</span>}
                </td>
                <td className="px-4 py-3 text-gray-600 text-sm">{q.project_name || '-'}</td>
                <td className="px-4 py-3 text-gray-600 text-sm">{q.quote_date}</td>
                <td className="px-4 py-3">{statusLabel(q.status)}</td>
                <td className="px-4 py-3 text-right text-sm font-medium text-blue-700">
                  {q.subtotal_usd > 0 ? `$${q.subtotal_usd.toFixed(2)}` : '—'}
                </td>
                <td className="px-4 py-3 text-right text-sm font-medium text-purple-700">
                  {q.subtotal_eur > 0 ? `€${q.subtotal_eur.toFixed(2)}` : '—'}
                </td>
                <td className="px-4 py-3 text-right text-sm font-medium text-gray-800">
                  {q.subtotal_try > 0 ? `₺${q.subtotal_try.toFixed(2)}` : '—'}
                </td>
                <td className="px-4 py-3">
                  {hakedisMap[q.id] ? (
                    <button onClick={() => navigate(`/hakedis/${q.id}`)}
                      className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded font-medium hover:bg-green-200">
                      ✓ {hakedisMap[q.id].hakedis_number}
                    </button>
                  ) : (
                    <button onClick={() => navigate(`/hakedis/${q.id}`)}
                      className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded font-medium hover:bg-orange-200">
                      + Hakediş
                    </button>
                  )}
                </td>
                <td className="px-4 py-3">
                  <ActionButtons q={q} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {quotes.length === 0 && (
          <div className="text-center py-12 text-gray-400">Henüz teklif oluşturulmamış</div>
        )}
      </div>

      {emailQuote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-1">Teklifi E-posta ile Gönder</h3>
            <p className="text-sm text-gray-500 mb-4">{emailQuote.quote_number} · {getCustomerName(emailQuote.customer_id)}</p>
            <form onSubmit={handleSendEmail}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Alıcı E-posta *</label>
                <input required type="email" value={emailForm.to} onChange={e => setEmailForm({...emailForm, to: e.target.value})} className="w-full border rounded px-3 py-2" />
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Mesaj (isteğe bağlı)</label>
                <textarea value={emailForm.message} onChange={e => setEmailForm({...emailForm, message: e.target.value})} className="w-full border rounded px-3 py-2" rows={3} />
              </div>
              <div className="flex gap-3 mt-6">
                <button type="submit" disabled={sendingEmail} className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50">
                  {sendingEmail ? 'Gönderiliyor...' : 'Gönder'}
                </button>
                <button type="button" onClick={() => setEmailQuote(null)} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 font-medium">İptal</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
